import { __rest } from "tslib";
import { api } from '@/api/api';
import { apis } from '@/config';
import { v4 as getUUID } from 'uuid';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { AiAsstWmInnerAbortReason, AiAsstWmChatMsgType, AiAsstWmChatMsgStatus, AiAsstWmBizType, } from '@/api/logical/ai_asst_wm';
import { getIn18Text, jsonSafeParse } from '@/api/utils';
export class AiAsstWmApiImpl {
    constructor() {
        this.name = 'aiAsstWmApiImpl';
        this.http = api.getDataTransApi();
        this.eventApi = api.getEventApi();
        this.systemApi = api.getSystemApi();
        this.dataTrackApi = api.requireLogicalApi(apis.dataTrackerApiImp);
    }
    init() {
        return this.name;
    }
    errorHandler(error) {
        var _a;
        this.eventApi.sendSysEvent({
            auto: true,
            eventSeq: 0,
            eventName: 'error',
            eventLevel: 'error',
            eventData: {
                title: ((_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.message) || (error === null || error === void 0 ? void 0 : error.message) || '网络错误',
                content: '',
                popupType: 'toast',
                popupLevel: 'error',
            },
        });
    }
    async get(url, req, config) {
        try {
            const { data } = await this.http.get(url, req, config);
            if (!data)
                throw {};
            if (!data.success) {
                throw data;
            }
            return data.data;
        }
        catch (error) {
            const { toastError = true } = config || {};
            toastError && this.errorHandler(error);
            return Promise.reject(error);
        }
    }
    async post(url, body, config) {
        config = Object.assign({ contentType: 'json', noEnqueue: false }, (config || {}));
        try {
            const { data } = await this.http.post(url, body, config);
            if (!data)
                throw {};
            if (!data.success) {
                throw data;
            }
            return data.data;
        }
        catch (error) {
            const { toastError = true } = config || {};
            toastError && this.errorHandler(error);
            return Promise.reject(error);
        }
    }
    async delete(url, req, config) {
        try {
            const { data } = await this.http.delete(url, req, config);
            if (!data)
                throw {};
            if (!data.success) {
                throw data;
            }
            return data.data;
        }
        catch (error) {
            const { toastError = true } = config || {};
            toastError && this.errorHandler(error);
            return Promise.reject(error);
        }
    }
    async connectChat(config) {
        const requestStartTime = Date.now();
        this.dataTrackApi.track('ai_asst_wm_request_start');
        const { onSendMsgUpdate, onReceiveMsgUpdate } = config;
        const url = this.getConnectChatUrl(config);
        const tempSendMsgId = this.createTempMsgId();
        const tempReceiveMsgId = this.createTempMsgId();
        let sendMsgId = null;
        let receiveMsgId = null;
        let receiveMsgContent = '';
        let receiveMsgFinished = false;
        const shouldCreateTempSendMsg = this.getShouldCreateTempSendMsg(config);
        if (shouldCreateTempSendMsg) {
            onSendMsgUpdate({
                msgId: tempSendMsgId,
                msg: this.createTempMsg({
                    msg_id: tempSendMsgId,
                    msg_type: AiAsstWmChatMsgType.COMMAND,
                    msg_status: AiAsstWmChatMsgStatus.FINISH,
                    content: this.getCommandContent(config),
                }),
            });
        }
        onReceiveMsgUpdate({
            msgId: tempReceiveMsgId,
            msg: this.createTempMsg({
                msg_id: tempReceiveMsgId,
                msg_type: AiAsstWmChatMsgType.RESPONSE,
                msg_status: AiAsstWmChatMsgStatus.PENDING,
                content: receiveMsgContent,
            }),
        });
        const receiveMsgErrorHandler = () => {
            const msgId = receiveMsgId || tempReceiveMsgId;
            onReceiveMsgUpdate({
                msgId,
                msg: this.createTempMsg({
                    msg_id: msgId,
                    msg_type: AiAsstWmChatMsgType.RESPONSE,
                    msg_status: AiAsstWmChatMsgStatus.ERROR,
                    content: getIn18Text('AIYAXITZMNSDYXZSSB'),
                }),
            });
            this.dataTrackApi.track('ai_asst_wm_request_error', {
                requestTime: Date.now() - requestStartTime,
            });
        };
        if (!url) {
            receiveMsgErrorHandler();
            return;
        }
        const abortController = config.abortController || new AbortController();
        abortController.signal.onabort = event => {
            var _a;
            let abortFromOuter = true;
            try {
                const reason = ((_a = event.target) === null || _a === void 0 ? void 0 : _a.reason) || event.reason;
                abortFromOuter = !AiAsstWmInnerAbortReason[reason];
            }
            catch (_b) {
            }
            if (abortFromOuter && !receiveMsgFinished) {
                receiveMsgErrorHandler();
            }
        };
        const streamParams = this.getStreamParams(config);
        return fetchEventSource(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Lingxi-Language': this.systemApi.getSystemLang(),
            },
            body: JSON.stringify(streamParams),
            signal: abortController.signal,
            openWhenHidden: true,
            onmessage: event => {
                const msg = jsonSafeParse(event.data);
                if (!msg) {
                    receiveMsgErrorHandler();
                    abortController.abort(AiAsstWmInnerAbortReason.MsgError);
                    return;
                }
                const hasError = msg.msg_status === AiAsstWmChatMsgStatus.ERROR;
                if (msg.msg_type === AiAsstWmChatMsgType.COMMAND) {
                    if (hasError) {
                        receiveMsgErrorHandler();
                        abortController.abort(AiAsstWmInnerAbortReason.MsgError);
                        return;
                    }
                    else {
                        onSendMsgUpdate({ msgId: tempSendMsgId, msg });
                        sendMsgId = msg.msg_id;
                    }
                }
                if (msg.msg_type === AiAsstWmChatMsgType.RESPONSE) {
                    if (hasError) {
                        onReceiveMsgUpdate({
                            msgId: receiveMsgId || tempReceiveMsgId,
                            msg: Object.assign(Object.assign({}, msg), { msg_id: msg.msg_id || receiveMsgId || tempReceiveMsgId, content: msg.content || getIn18Text('AIYAXITZMNSDYXZSSB') }),
                        });
                        abortController.abort(AiAsstWmInnerAbortReason.MsgError);
                        this.dataTrackApi.track('ai_asst_wm_request_error', {
                            requestTime: Date.now() - requestStartTime,
                        });
                        return;
                    }
                    else {
                        receiveMsgContent += msg.content;
                        onReceiveMsgUpdate({
                            msgId: receiveMsgId || tempReceiveMsgId,
                            msg: Object.assign(Object.assign({}, msg), { content: receiveMsgContent }),
                        });
                        receiveMsgId = msg.msg_id;
                        if (msg.msg_status === AiAsstWmChatMsgStatus.FINISH) {
                            receiveMsgFinished = true;
                            this.dataTrackApi.track('ai_asst_wm_request_success', {
                                requestTime: Date.now() - requestStartTime,
                            });
                        }
                    }
                }
            },
            onopen: async (res) => {
                if (!res.ok) {
                    throw new Error('onopen failed');
                }
            },
            onerror: error => {
                throw error;
            },
        }).catch(error => {
            receiveMsgErrorHandler();
            abortController.abort(AiAsstWmInnerAbortReason.CatchedError);
            window.console['error']('xxxx-fetchEventSource-catch-error', error, config);
        });
    }
    getConnectChatUrl(config) {
        const { type, params, isRegenerate } = config;
        if (isRegenerate)
            return this.systemApi.getUrl('aiAsstWmStreamRegenerate');
        if (type === AiAsstWmBizType.EMAIL_SUMMARY) {
            return this.systemApi.getUrl('aiAsstWmStreamEmailSummary');
        }
        if (type === AiAsstWmBizType.EMAIL_REPLY) {
            if (params.assistant_id && params.chat_id && params.option_info) {
                return this.systemApi.getUrl('aiAsstWmStreamEmailReplyRoundTwo');
            }
            else {
                return this.systemApi.getUrl('aiAsstWmStreamEmailReplyRoundOne');
            }
        }
        if (type === AiAsstWmBizType.AI_QUESTION) {
            return this.systemApi.getUrl('aiAsstWmStreamAiQuestion');
        }
        return null;
    }
    getShouldCreateTempSendMsg(config) {
        return !config.isRegenerate;
    }
    getCommandContent(config) {
        const { type, params, isRegenerate } = config;
        if (isRegenerate)
            return '';
        const contentMap = {
            [AiAsstWmBizType.EMAIL_SUMMARY]: getIn18Text('ZONGJIECIYJ'),
            [AiAsstWmBizType.EMAIL_REPLY]: getIn18Text('HUIFUCIYJ'),
        };
        if (type === AiAsstWmBizType.EMAIL_REPLY && params.option_info) {
            return params.option_info;
        }
        if (type === AiAsstWmBizType.AI_QUESTION) {
            return params.content;
        }
        return contentMap[type] || '';
    }
    getStreamParams(config) {
        const { type, params } = config;
        if (type === AiAsstWmBizType.AI_QUESTION) {
            const { content } = params, restParams = __rest(params, ["content"]);
            return Object.assign(Object.assign({}, restParams), { content: [{ content, role: 'human' }] });
        }
        return params;
    }
    createTempMsg(payload) {
        return Object.assign({ assistant_id: '', chat_id: '', msg_id: this.createTempMsgId(), msg_type: AiAsstWmChatMsgType.COMMAND, msg_status: AiAsstWmChatMsgStatus.FINISH, msg_seq_number: '', biz_id: '', biz_type: AiAsstWmBizType.EMAIL_SUMMARY, content: '', content_language: '', create_time: Date.now().toLocaleString(), isTemp: true }, payload);
    }
    createTempMsgId() {
        return getUUID();
    }
    createGuidanceMsg(config) {
        const { params, onReceiveMsgUpdate } = config;
        const fromUser = params.source === 'USER';
        const tempReceiveMsgId = this.createTempMsgId();
        const tempReceiveMsg = this.createTempMsg({
            biz_type: AiAsstWmBizType.PROACTIVE_GUIDE,
            msg_id: tempReceiveMsgId,
            msg_type: AiAsstWmChatMsgType.RESPONSE,
            msg_status: AiAsstWmChatMsgStatus.PENDING,
            content: '',
        });
        if (fromUser) {
            onReceiveMsgUpdate({
                msgId: tempReceiveMsgId,
                msg: tempReceiveMsg,
            });
        }
        const req = Object.assign({ biz_id: '', source: '' }, params);
        const url = this.systemApi.getUrl('aiAsstWmCreateGuidanceMsg');
        this.post(url, req, { toastError: false }).then((msg) => {
            if (fromUser) {
                if (msg) {
                    onReceiveMsgUpdate({
                        msgId: tempReceiveMsgId,
                        msg,
                    });
                }
                else {
                    onReceiveMsgUpdate({
                        msgId: tempReceiveMsgId,
                        msg: Object.assign(Object.assign({}, tempReceiveMsg), { msg_status: AiAsstWmChatMsgStatus.ERROR, content: getIn18Text('AIYAXITZMNSDYXZSSB') }),
                    });
                }
            }
            else {
                if (msg) {
                    onReceiveMsgUpdate({
                        msgId: msg.msg_id,
                        msg,
                    });
                }
            }
        });
    }
    createAiAssistantChat(params) {
        return this.post(this.systemApi.getUrl('createAiAssistantChat'), params);
    }
    getAiAssistantInputLimit() {
        return this.get(this.systemApi.getUrl('getAiAssistantInputLimit'));
    }
    getAiAssistantQuotaRemain(params) {
        return this.get(this.systemApi.getUrl('getAiAssistantQuotaRemain'), params);
    }
    getAiAssistantMessageHistory(params) {
        return this.post(this.systemApi.getUrl('getAiAssistantMessageHistory'), params);
    }
    postAiAssistantMessageFeedback(params) {
        return this.post(this.systemApi.getUrl('postAiAssistantMessageFeedback'), params);
    }
    cancelAiAssistantMessageFeedback(params) {
        return this.post(this.systemApi.getUrl('cancelAiAssistantMessageFeedback'), params);
    }
    deleteAiAssistanMessage(params) {
        return this.delete(this.systemApi.getUrl('deleteAiAssistanMessage') + `?msg_id=${params.msg_id}`, params);
    }
    translateAiAssistantText(params) {
        return this.post(this.systemApi.getUrl('translateAiAssistantText'), params);
    }
    chatWithDeepSeek(params, config) {
        return this.post(this.systemApi.getUrl('aiAsstWmChatWithDeepSeek'), params, config);
    }
    pollDeepSeekMsg(params, config) {
        return this.post(this.systemApi.getUrl('aiAsstWmPollDeepSeekMsg'), params, config);
    }
    aiAsstPostMessageStatReddot(params) {
        return this.post(this.systemApi.getUrl('aiAsstPostMessageStatReddot'), params);
    }
    aiAsstClearReddotMessage(params) {
        return this.post(this.systemApi.getUrl('aiAsstClearReddotMessage'), params);
    }
    aiAsstDefaultConfig() {
        return this.get(this.systemApi.getUrl('aiAsstDefaultConfig'));
    }
}
const aiAsstWmApiImpl = new AiAsstWmApiImpl();
api.registerLogicalApi(aiAsstWmApiImpl);
export default aiAsstWmApiImpl;
